/**
 * Reusable primitives for SSE-driven projections that need to be
 * replay-safe (idempotent + monotonic).
 */

/**
 * Builds a clamp-to-max function for a finite-state machine whose states
 * have a known ordering. Returns a closure that, given the previous and
 * next state, returns the *later* one. Prevents lifecycle regression on
 * `/control/seek` replay — re-firing an earlier-state event after the
 * projection has reached a later state is a no-op.
 *
 *   const docOrder = ["Created", "Hashed", "Attested", "Transmitted", "Validated"] as const;
 *   const monoDoc = monotonicState(docOrder);
 *   monoDoc("Validated", "Created")   // → "Validated"  (no regression)
 *   monoDoc("Created",   "Attested")  // → "Attested"   (forward progression)
 *   monoDoc(undefined,   "Created")   // → "Created"    (initial)
 *
 * Use cases:
 *   - Document lifecycle (Created → Hashed → … → Validated)
 *   - Invoice FSM (DRAFT → TRANSMITTED → … → MATCHED)
 *   - LC lifecycle (Drafted → Issued → … → Settled)
 *
 * An off-order `next` value (not present in `order`) is returned as-is —
 * the caller is assumed to be authoritative. This keeps the helper
 * permissive when the projection sees an event from a state machine
 * extension that the order table doesn't know about.
 */
export function monotonicState<S extends string>(order: readonly S[]) {
  const index = new Map<S, number>(order.map((s, i) => [s, i] as const));
  return function clampToMax(prev: S | undefined, next: S): S {
    if (prev === undefined) return next;
    const a = index.get(prev);
    const b = index.get(next);
    if (a === undefined || b === undefined) return next;
    return b > a ? next : prev;
  };
}
